import { observable, computed, action, makeObservable } from 'mobx';

import { Class, Optional } from 'chaika-common-types';

import { removeNilAndEmptyKeys } from '@utils/removeNilAndEmptyKeys';

import { LongList } from './LongList';

export class SearchList<LP, CP, FP extends object, I = string, LFP = any> {
    @observable protected result: LongList<LP, CP, LFP, I>;
    @observable protected filterParams: FP = {} as FP;

    constructor(ListClass: Class<LongList<LP, CP, LFP, I>>, props?: any) {
        makeObservable(this);

        this.result = new ListClass(props);
    }

    @computed
    public get values(): LP[] {
        return this.result.values;
    }

    @computed
    public get count(): number {
        return this.result.count;
    }

    @computed
    public get offset(): number {
        return this.result.offset;
    }

    @computed
    public get ids(): I[] {
        return this.result.ids;
    }

    @computed
    public get searchParams(): FP {
        return {
            ...this.result.queryParams,
            ...this.filterParams,
        };
    }

    @computed
    public get queryParams(): FP {
        return this.searchParams;
    }

    @computed
    public get searchParamsSet(): boolean {
        return Object.keys(this.filterParams).length > 0;
    }

    public has(id: I): boolean {
        return this.result.has(id);
    }

    public getValuesByIds(ids: I[]): LP[] {
        return this.result.getValuesByIds(ids);
    }

    public getFilteredValues(params: any): LP[] {
        return this.result.getFilteredValues(params);
    }

    public get(id: I): Optional<LP> {
        return this.result.get(id);
    }

    @action
    public setResult(result: CP[], count: number): void {
        this.result.set(result);
        this.result.count = count;
    }

    @action
    public addNext(params: CP[]): void {
        this.result.addNext(params);
    }

    @action
    public add(params: CP[]): void {
        this.result.add(params);
    }

    @action
    public reset(): void {
        this.result.reset();
        this.filterParams = {} as FP;
    }

    @action
    public update(id: I, value: CP): void {
        this.result.update(id, value);
    }

    @action
    public updateFilterParams({ offset, ...params }: FP & { offset?: number }): void {
        this.result.reset();
        this.filterParams = { ...this.filterParams, ...params };
    }

    @action
    public setFilterParams({ offset, ...params }: FP & { offset?: number }): void {
        this.result.reset();
        this.filterParams = removeNilAndEmptyKeys(params);
    }

    @action
    public remove(id: I): void {
        this.result.remove(id);
    }

    public needLoadNext(targetOffset: number): boolean {
        return !targetOffset || this.offset < targetOffset;
    }

    public needLoadMore(): boolean {
        return this.offset < this.count;
    }
}
